import { doc, runTransaction, Timestamp, increment, deleteField } from 'firebase/firestore';
import moment from 'moment-timezone';
import { firestore } from '../firebaseConfig';
import { nextSrsStage, nextReviewDateForStage } from '../utils/srs';

/**
 * Atomically updates an item (word or sentence) after a review session.
 * Adjusts SRS stage, next review date, correct/wrong counts for the item,
 * AND increments the relevant daily reviewed count for the user.
 *
 * @param {string} userId - The ID of the user.
 * @param {string} itemId - The ID of the word or sentence reviewed.
 * @param {'words' | 'sentences'} itemType - The type of item being reviewed.
 * @param {'correct' | 'wrong'} action - The action to perform on the SRS stage.
 * @returns {Promise<{status: string, itemId: string, newStage: number}>} - Success status, item ID, and new stage.
 * @throws {Error} - Throws errors for invalid params, user/item not found, subscription fails, or transaction fails.
 */
export const updateItemReview = async (userId, itemId, itemType, action, isSubscribed, expectedReviewTotal = null, playsBeforeSubmit = 0, audioDuration = 0) => {
  const functionStartTime = Date.now();

  // 1. Validate parameters before starting transaction
  if (!userId || !itemId) {
    throw new Error('userId and itemId are required.');
  }
  if (itemType !== 'words' && itemType !== 'sentences') {
    throw new Error("Invalid itemType. Must be 'words' or 'sentences'.");
  }
  if (action !== 'correct' && action !== 'wrong') {
    throw new Error("Invalid action. Must be 'correct' or 'wrong'.");
  }

  // 2. Define references dynamically
  const userRef = doc(firestore, 'users', userId);
  const itemSubcollectionPath = `user_${itemType}`;
  const itemRef = doc(firestore, 'users', userId, itemSubcollectionPath, itemId);

  const reviewCountDocName = `reviewed_${itemType}_today`;
  const reviewedTodayRef = doc(firestore, 'users', userId, 'metadata', reviewCountDocName);

  try {
    // --- Execute the core logic within a Firestore Transaction ---
    const newSrsStage = await runTransaction(firestore, async (transaction) => {
      // 3. Transactionally get required documents
      const userSnapshot = await transaction.get(userRef);
      const itemSnapshot = await transaction.get(itemRef);
      const reviewedTodaySnapshot = await transaction.get(reviewedTodayRef);

      if (!userSnapshot.exists()) {
        throw new Error(`User ${userId} not found`);
      }
      if (!itemSnapshot.exists()) {
        const notFoundError = new Error(`${itemType.slice(0, -1)} ${itemId} not found in user's list.`);
        notFoundError.code = 'item-in-review-not-found';
        throw notFoundError;
      }

      const userData = userSnapshot.data();
      const itemData = itemSnapshot.data();
      const currentStage = itemData.srs_stage;

      // Guard against reviewing a skipped item (e.g. skipped in another tab after session loaded)
      if (itemData.skipped) {
        const err = new Error('This item has been skipped and cannot be reviewed.');
        err.code = 'item-is-skipped';
        throw err;
      }

      // Guard against double-review from concurrent sessions
      if (expectedReviewTotal !== null) {
        const currentTotal = (itemData.correct || 0) + (itemData.wrong || 0);
        if (currentTotal !== expectedReviewTotal) {
          const err = new Error('This item was already reviewed in another session.');
          err.code = 'already-reviewed';
          throw err;
        }
      }
      const itemLevel = itemData.level;

      // 4. Perform subscription check

      // Define premium requirement conditions based on item type and level
      let requiresPremium = false;
      if (!isSubscribed) {
        if ((itemType === 'sentences' || itemType === 'words') && itemLevel > 0 && itemLevel < 5) {
          requiresPremium = true;
        }
      }

      if (requiresPremium) {
        const error = new Error(`Premium required to review Level ${itemLevel} ${itemType.slice(0, -1)}.`);
        error.code = 'premium-required';
        error.level = itemLevel;
        throw error;
      }

      // 5. Calculate date-dependent fields for daily counter
      const userTimezone = userData.settings?.timezone || 'UTC';
      const todayDate = moment().tz(userTimezone).format('YYYY-MM-DD');

      // Guard against submitting reviews while on vacation
      if (userData.settings?.vacation_started_at) {
        const err = new Error('Reviews cannot be submitted while vacation mode is active.');
        err.code = 'vacation-mode';
        throw err;
      }

      // Guard against exceeding daily review limit (catches concurrent sessions)
      const maxReviews = itemType === 'words'
        ? (userData.settings?.words_reviews_per_day ?? -1)
        : (userData.settings?.sentence_reviews_per_day ?? -1);
      if (maxReviews !== -1) {
        const reviewedTodayData = reviewedTodaySnapshot.exists() ? reviewedTodaySnapshot.data() : {};
        const reviewedCount = reviewedTodayData?.data?.[todayDate] || 0;
        if (reviewedCount >= maxReviews) {
          const err = new Error('Daily review limit reached.');
          err.code = 'daily-limit-reached';
          throw err;
        }
      }

      // 6. Calculate new SRS stage and prepare item update based on action
      const reviewedAt = Timestamp.now();
      const newStage = nextSrsStage(currentStage, action);
      const nextReview = Timestamp.fromDate(nextReviewDateForStage(newStage));

      const itemUpdateData =
        action === 'correct'
          ? {
              srs_stage: newStage,
              next_review: nextReview,
              correct: increment(1),
              last_reviewed_at: reviewedAt,
              last_correct_at: reviewedAt,
              last_mistake_at: deleteField(),
            }
          : {
              srs_stage: newStage,
              next_review: nextReview,
              wrong: increment(1),
              last_reviewed_at: reviewedAt,
              last_wrong_at: reviewedAt,
              last_mistake_at: reviewedAt,
            };


      // 8. Update metadata stats documents within the transaction
      const srsRef = doc(firestore, 'users', userId, 'metadata', 'counts_by_srs');
      const accuracyRef = doc(firestore, 'users', userId, 'metadata', 'review_accuracy');
      
      // Update SRS stage counts if changed
      if (currentStage !== newStage) {
        const srsUpdate = {
          [itemType]: {
            [currentStage]: increment(-1),
            [newStage]: increment(1)
          }
        };
        transaction.set(srsRef, srsUpdate, { merge: true });
      }
      
      // Update accuracy stats
      const accuracyUpdate = {
        [itemType]: {
          [action === 'correct' ? 'correct' : 'wrong']: increment(1)
        }
      };
      transaction.set(accuracyRef, accuracyUpdate, { merge: true });

      // Total listening time (review + learn) — one listen per submission,
      // replays within a review aren't counted.
      const listeningRef = doc(firestore, 'users', userId, 'metadata', 'listening_stats');
      transaction.set(listeningRef, { [itemType]: { seconds: increment(audioDuration) } }, { merge: true });

      // Plays-per-day → numerator of the "avg listens before answering" ratio.
      // Denominator is the existing reviewed_{type}_today (submissions), so we
      // only store plays. Mirrors the reviewed_{type}_today daily-count doc.
      // Floor at 1: a review always involves at least one listen, so even if no
      // play was captured (e.g. autoplay blocked) it counts as one — keeping this
      // in step with the +1 to reviewed_{type}_today so the ratio never drops below 1.
      const playedTodayRef = doc(firestore, 'users', userId, 'metadata', `played_${itemType}_today`);
      transaction.set(playedTodayRef, { data: { [todayDate]: increment(Math.max(1, playsBeforeSubmit)) } }, { merge: true });
      
      // 9. Queue ALL writes for the transaction
      // a) Update the item document in the correct subcollection
      transaction.update(itemRef, itemUpdateData);

      // b) Update the reviewed_today metadata document
      transaction.set(reviewedTodayRef, { data: { [todayDate]: increment(1) } }, { merge: true });

      return newStage; // Return new stage from transaction block
    }); // --- End of Transaction ---

    return { status: 'fulfilled', itemId: itemId, newStage: newSrsStage };
  } catch (error) {
    console.error(`[${functionStartTime}] Transaction FAILED for ${action} review of ${itemType.slice(0, -1)} ${itemId}:`, error);

    // Handle specific errors
    if (['premium-required', 'item-in-review-not-found', 'already-reviewed', 'item-is-skipped', 'vacation-mode', 'daily-limit-reached'].includes(error.code)) {
      throw error;
    }
    if (error.code === 'permission-denied') {
      const permissionError = new Error(`Permission denied during ${itemType} review update.`);
      permissionError.code = `permission-denied-${action}review-${itemType}`;
      throw permissionError;
    }

    // Throw a generic error or the original one
    throw new Error(`Failed to update ${action} ${itemType.slice(0, -1)} review: ${error.message || error}`);
  }
};
